<template>
  <div class="w-full pt-5 pb-5">
    <template>
      <div class="flex items-center">
        <div class="border-r border-romanSilver h-6 flex">
          <BackButton label="Back" @onClick="
            $router.push({
              name: 'EssMSSPerformance',
            })
            " variant="secondary" class="ml-2" />
        </div>
        <h1 class="text-xl text-left font-extrabold mx-4">Appraisal Details</h1>
        <Breadcrumb :items="breadcrumbs" />
      </div>
    </template>
    <template v-if="isLoading">
      <div style="height: 100%" class="mt-20">
        <loader size="xxs" :loader-image="false" />
      </div>
    </template>
    <template v-else>
      <template>
        <div class="p-5 flex flex-col shadow m-5 rounded-md gap-4">
          <template>
            <div class="flex justify-between items-center">
              <span class="font-bold text-lg leading-5 tracking-normal capitalize text-darkPurple">
                Appraisal Details
              </span>
            </div>
          </template>
          <template>
            <div class="border border-dashed bg-white border-romanSilver rounded-md gap-4 p-3 mt-4">
              <div class="flex justify-between items-center w-full gap-4 pb-2 cursor-pointer"
                @click="handleShowDetails">
                <span class="font-black text-xs leading-5 uppercase tracking-wider text-romanSilver">
                  Performance Details
                </span>
                <span class="w-9/12 border-t border-solid border-romanSilver h-0" />
                <Icon :icon-name="showDetails ? 'chevronDown' : 'chevronRight'"
                  class-name="text-darkPurple mx-auto self-center" size="xms" />
              </div>
              <div class="flex flex-col gap-2" :class="{ hidden: !showDetails }">
                <div class="flex p-3 shadow rounded-md gap-2">
                  <Icon size="s" icon-name="doc-cycle" class-name="text-romanSilver self-start pt-1" />
                  <div class="flex flex-col">
                    <span class="font-black leading-5 text-xs tracking-wide text-darkPurple uppercase">
                      {{ agreementData.cycle }}
                    </span>
                    <span class="font-bold text-sm leading-6 tracking-wide text-jet uppercase">
                      {{ agreementData.description }}
                    </span>
                  </div>
                </div>
                <div 
                  class="grid gap-4 w-full"
                  style="grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));"
                >
                  <div class="flex p-3 shadow rounded-md gap-2">
                    <Icon size="xs" icon-name="calendar" class-name="text-romanSilver self-start pt-1" />
                    <div class="flex flex-col">
                      <span class="font-black leading-5 text-xs tracking-wide text-carrotOrange uppercase">
                        Period
                      </span>
                      <span class="font-bold text-sm leading-6 tracking-wide text-jet uppercase">
                        {{
                          $DATEFORMAT(
                            new Date(agreementData.period.start),
                            "MMMM dd, yyyy"
                          )
                        }}
                        -
                        {{
                          $DATEFORMAT(
                            new Date(agreementData.period.end),
                            "MMMM dd, yyyy"
                          )
                        }}
                      </span>
                    </div>
                  </div>
                  <div class="flex p-3 shadow rounded-md gap-2"
                    v-for="(cycle, index) in agreementCycles" :key="index"
                      :class="{ 'opacity-40': !cycle.active }"
                      :style="currentQAppraisal === cycle.id ? 'background-color: #9CA3AF;' : ''"
                    >
                    <Icon class-name="text-romanSilver self-start pt-1" size="xs" icon-name="reviewicon" />
                    <div class="flex flex-col flex-grow">
                      <span class="font-black leading-5 text-xs tracking-wide text-blueCrayola uppercase">
                        {{ cycle.name }}
                      </span>
                      <span class="font-bold text-sm leading-6 flex tracking-wide text-jet uppercase">
                        {{
                          $DATEFORMAT(new Date(cycle.start), "MMMM dd, yyyy")
                        }}
                        -
                        {{ $DATEFORMAT(new Date(cycle.end), "MMMM dd, yyyy") }}
                        <span v-if="extendedCycle">
                          <span class="ml-4 flex" v-if="extendedCycle.appraisalCycleId === cycle.id">
                            <p class="text-sm uppercase text-mediumSeaGreen italic" style="margin-top: 0.15rem">
                              Extended
                            </p>
                            <Icon class="ml-3" icon-name="icon-info" size="xs" style="margin-top: -0.15rem" />
                          </span>
                        </span>
                        <span 
                          class="font-bold ml-6 text-xs leading-6 flex tracking-wide text-mediumSeaGreen uppercase cursor-pointer"
                          @click="viewCycle(cycle.id)">
                          View
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </template>
      <template>
        <div class="w-full px-5">
          <div class="w-full">
            <Card class="p-5 pt-0">
              <CardFooter reloadcard showcalendar :show-bulk-actions="showBulk" :bulk-options="bulkActionOptions"
                @action="performBulkAction($event)" @reload="reload()" @searchResult="handleSearch($event)" />
            </Card>
          </div>
          <div>
            <Table :headers="AppHeader" :items="reorderedEmployees" aria-label="advance Table" class="w-full py-2"
              :has-checkbox="true" :has-number="true" :loading="loadingTable" :pagination-list="metaData"
              style="width: 100%" id="printMe" @page="handlePage($event)" @itemsPerPage="handleItemsPerPage($event)"
               @rowSelected="displayRow($event)" page-sync>
              <template v-slot:item="{ item }">
                <div v-if="item.photo">
                  <div>
                    <img class="mr-3" :src="item.data.photo" v-if="item.data.photo" alt="user photo"
                      style="height: 30px; width: 30px; border-radius: 5px" />
                    <div style="height: 30px; width: 30px; border-radius: 5px"
                      class="text-blueCrayola mr-3 flex justify-center border text-center font-semibold pt-1" v-else>
                      {{ $getInitials(`${item.data.name}`) }}
                    </div>
                  </div>
                </div>
                <div v-if="item.name" class="flex flex-col whitespace-nowrap font-semibold text-sm leading-5">
                  <span class="text-darkPurple capitalize">{{
                    item.data.name
                  }}</span>
                  <div class="flex">
                    <span class="text-text-romanSilver uppercase" style="font-size: 10px">{{ item.data.function
                      }}</span>
                    <span class="text-blueCrayola uppercase ml-3" style="font-size: 10px">{{ item.data.designation
                      }}</span>
                  </div>
                  <span class=" text-carrotOrange" v-if="item.data.isDottedReporting">[Dotted Report]</span>
                </div>
                <div v-if="item.selfAppraisal">
                  <span class="text-left text-sm leading-6 font-normal text-darkPurple">{{ item.data.selfAppraisal
                    }}</span>
                </div>
                <div v-if="item.managerAppraisal">
                  <span class="text-left text-sm leading-6 font-normal text-darkPurple">{{ item.data.managerAppraisal
                  }}</span>
                </div>
                <div v-if="item.dottedLineManagerAppraisal">
                  <span class="text-left text-sm leading-6 font-normal text-darkPurple">{{
                    item.data.dottedLineManagerAppraisal
                  }}</span>
                </div>
                <div v-if="item.approvedScore">
                  <span class="text-left text-sm leading-6 font-normal text-darkPurple">{{ item.data.approvedScore
                    }}</span>
                </div>
                <div v-if="item.status">
                  <div class="flex flex-col gap-1">
                    <div class="flex flex-grow" v-if="!item.data.isDottedReporting && item.data.managerApprovedBy ||
                    item.data.isDottedReporting && item.data.dottedApprovedBy">
                      <Badge label="Submitted" variant="primary" :background-color="`rgba(19, 181, 106, 0.15)`"
                        :color="'#13B56A'" class="capitalize" style="
                          margin-right: 12px;
                          font-size: 14px;
                          padding: 12px 10px;
                          border-radius: 5px;
                        " />
                    </div>

                    <div class="flex flex-grow" v-if="item.data.status === 'pending' && (!item.data.isDottedReporting && !item.data.managerApprovedBy ||
                    item.data.isDottedReporting && !item.data.dottedApprovedBy)">
                      <Badge label="In-Review" variant="primary" :background-color="`rgba(233, 147, 35, 0.15)`"
                        :color="'rgba(233, 147, 35, 1)'" class="capitalize" style="
                          margin-right: 12px;
                          font-size: 14px;
                          padding: 12px 10px;
                          border-radius: 5px;
                        " />
                    </div>

                    <div class="flex flex-grow" v-if="item.data.status === 'not submitted'">
                      <Badge label="not submitted" variant="primary" :background-color="`rgba(234, 60, 83, 0.15)`"
                        :color="'#EA3C53'" class="capitalize" style="
                          margin-right: 12px;
                          font-size: 14px;
                          padding: 12px 10px;
                          border-radius: 5px;
                        " />
                    </div>
                    <div class="w-full flex justify-start">
                      {{
                        item.data.submitted === ""
                          ? "---"
                          : $DATEFORMAT(
                            new Date(item.data.submitted),
                            "MMMM dd, yyyy"
                          )
                      }}
                    </div>
                  </div>
                </div>
                <div class="cursor-pointer" v-if="item.id">
                  <Menu left @click.stop="" top="-120" margin="29">
                    <template v-slot:title>
                      <Icon icon-name="more_icon" size="xs" />
                    </template>
                    <template>
                      <div class="flex flex-col w-52 h-18 p-2 justify-start items-start cursor-pointer">
                        <div class="hover:bg-ghostWhite w-full rounded-md" @click="
                          $router.push({
                            name: 'EssPerformanceEmployeeAppraisals',
                            params: {
                              year: item.data.year,
                              userId: item.data.id,
                              cycle: item.data.cycleName,
                              goalId: item.data.goal,
                              directReportYear: $route.params.year,
                              directReportId: $route.params.id,
                              QAppraisal: currentQAppraisal
                            },
                          })
                          " v-if="
                            openReviewAction ||
                            item.data.status !== 'not submitted'
                          ">
                          <div class="flex justify-start items-center p-2 gap-2">
                            <Icon icon-name="icon-eye" class-name="text-blueCrayola" size="xs" />
                            <span class="text-darkPurple text-sm leading-5 font-normal">
                              Review
                            </span>
                          </div>
                        </div>
                        <div class="hover:bg-ghostWhite w-full rounded-md cursor-pointer">
                          <div class="flex justify-start items-center p-2 gap-2" v-if="
                            item.data.status === 'pending' && (!item.data.isDottedReporting && item.data.managerAppraisal !== '---' 
                            && !item.data.managerApprovedBy || item.data.isDottedReporting && 
                            item.data.dottedLineManagerAppraisal !== '---' && !item.data.dottedApprovedBy)
                          " @click="approveAppraisal(item.data)">
                            <Icon icon-name="icon-check" size="xms" class-name="mr-2 w-5 h-5" />
                            <span class="text-darkPurple text-sm leading-5 font-normal">
                              Approve
                            </span>
                          </div>
                        </div>

                        <div class="hover:bg-ghostWhite w-full rounded-md cursor-pointer" @click="
                          $router.push({
                            name: 'EssEmployeeStatusReports',
                            params: {
                              id: item.data.id,
                              year: $route.params.year,
                            },
                          })
                          ">
                          <div class="flex justify-start items-center p-2 gap-2">
                            <Icon icon-name="file-document" class-name="text-carrotOrange" size="xs" />
                            <span class="text-darkPurple text-sm leading-5 font-normal">
                              Performance Reports
                            </span>
                          </div>
                        </div>

                        <!-- <div class="hover:bg-ghostWhite w-full rounded-md cursor-pointer" v-if="
                          openCommentsAction ||
                          item.data.status !== 'not submitted'
                        ">
                          <div class="flex justify-start items-center p-2 gap-2" @click="
                            getComments(item.data.agreementId, item.data.id)
                            ">
                            <Icon icon-name="comment-outline" class-name="text-carrotOrange" size="xs" />
                            <span class="text-darkPurple text-sm leading-5 font-normal">
                              Comment
                            </span>
                          </div>
                        </div> -->
                      </div>
                    </template>
                  </Menu>
                </div>
              </template>
            </Table>
          </div>
        </div>
      </template>
    </template>

    <RightSideBar v-if="commentModal" @submit="sendComment" @close="commentModal = false, comment = ''" submit="Save"
      button-class="bg-dynamicBackBtn text-white">
      <template v-slot:title>
        <span class="text-darkPurple font-bold text-lg leading-5 p-0">Comments</span>
      </template>
      <div class="w-full flex mb-7">
        <div class="flex flex-col w-full" style="min-height: 70vh">
          <CommentsBox :comments="allComments" :sender-id="senderId" @markRead="markAsRead($event)"
            @deleteComment="deleteComments($event)" :loading="loadComments" />
          <div class="-mb-12" style="margin-top: auto">
            <CTextarea placeholder="--Message--" variant="primary" class="mt-2 rounded-lg" value="" :row="8"
              :height="90" :col="30" :input="null" v-model="comment" />
          </div>
        </div>
      </div>
    </RightSideBar>
  </div>
</template>

<script>
import BackButton from "@scelloo/cloudenly-ui/src/components/back-button";
import Breadcrumb from "@scelloo/cloudenly-ui/src/components/bread-crumb";
import Table from "@scelloo/cloudenly-ui/src/components/table";
import CTextarea from "@scelloo/cloudenly-ui/src/components/text-area";
import Badge from "@scelloo/cloudenly-ui/src/components/badge";
import Card from "@scelloo/cloudenly-ui/src/components/card";
import Menu from "@scelloo/cloudenly-ui/src/components/Menu";
import CardFooter from "@/components/CardFooter";
import RightSideBar from "@/components/RightSideBar";
import CommentsBox from "@/components/commentsBox";
import paramsMixin from "@/utilities/paramsMixin";
import timeFormatMixin from "@/utilities/timeFormatMixin";

export default {
  mixins: [paramsMixin, timeFormatMixin],
  components: {
    BackButton,
    Breadcrumb,
    Badge,
    RightSideBar,
    CommentsBox,
    Table,
    CTextarea,
    Card,
    Menu,
    CardFooter,
  },
  data() {
    return {
      isLoading: true,
      showDetails: true,
      loadingTable: true,
      breadcrumbs: [
        {
          disabled: false,
          text: "Performance",
          href: "Performance",
          id: "Performance",
        },
        {
          disabled: false,
          text: "Performance Appraisal",
          href: "Performance Appraisal",
          id: "PerformanceAppraisal",
        },
        {
          disabled: false,
          text: "Appraisal Details",
          href: "AppraisalDetails",
          id: "AppraisalDetails",
        },
      ],
      AppHeader: [
        { title: "", value: "photo", width: "6" },
        { title: "Name", value: "name" },
        { title: "Self Appraisal", value: "selfAppraisal" },
        { title: "Line Manager", value: "managerAppraisal" },
        { title: "Approved Score", value: "approvedScore" },
        { title: "Status", value: "status" },
        { title: "", value: "id", image: true },
      ],
      items: [],
      midTermAppraisalStructure: '',
      isDottedManager: false,
      showBulk: false,
      bulkActionOptions: [
        {
          icon: "icon-check",
          text: "Approve",
          action: "approve Appraisal",
        },
      ],
      paginationList: {
        page: 1,
        lastPage: 1,
        total: 1,
        from: 1,
        to: 1,
      },
      agreementData: {
        title: null,
        cycle: null,
        description: null,
        period: { start: null, end: null },
      },
      templateArr: [],
      agreementCycles: [],
      employees: [],
      sharedAppraisals: [],
      cyclesArr: [],
      commentModal: false,
      allComments: [],
      receiverId: "",
      commentAgreementId: "",
      loadComments: false,
      comment: "",
      selectedEmployees: [],
      extendedCycle: {},
      activeCycle: "",
      searchQuery: "",
      metaData: {},
      itemsPerPage: null,
      numberOfPage: null,
      openReviewAction: false,
      openCommentsAction: false,
      dottedReportingAppraisal: false,
      senderId: '',
      currentQAppraisal: '',
    };
  },
  computed: {
    reorderedEmployees() {
      const hasDottedReporting = this.employees.filter(item => item.isDottedReporting);
      const nonDottedReporting = this.employees.filter(item => !item.isDottedReporting);

      if (hasDottedReporting.length > 0) {
        const reOrderEmployees = [...nonDottedReporting, ...hasDottedReporting];

        return reOrderEmployees
      }
      return this.employees
    }
  },
  methods: {
    handleItemsPerPage(itemNo) {
      this.itemsPerPage = itemNo;
      this.queryUpdate();
    },

    handlePage(pageNumber) {
      this.numberOfPage = pageNumber;
      this.queryUpdate();
    },

    queryUpdate() {
      this.loadingTable = true;
      const search = this.searchQuery ? `${this.searchQuery}` : "";
      const pageNumber = this.numberOfPage
        ? `&page=${this.numberOfPage}`
        : `&page=${this.metaData.page}`;
      const itemPage = this.itemsPerPage ? `&perPage=${this.itemsPerPage}` : "";
      this.getUsersDirectReports(`${pageNumber}${itemPage}${search}`);
    },

    handleSearch(value) {
      this.searchQuery = `&search=${value}`;
      this.loadingTable = true;
      this.getUsersDirectReports(this.searchQuery);
    },

    viewCycle(value) {
      this.agreementCycles.forEach((cycle) => {
        if (cycle.id === value) {
          this.activeCycle = cycle.name;
          this.currentQAppraisal = value;
        }
      });
      this.sharedAppraisals = [];
      this.employees = [];
      this.loadingTable = true;
      this.getDirectReports();
    },

    reload() {
      this.loadingTable = true;
      this.getDirectReports();
    },

    displayRow(value) {
      if (value.length > 0) {
        this.selectedEmployees = value.filter(
          (employee) =>
            employee.managerAppraisal !== "---" && !employee.approved
        );
        if (this.selectedEmployees.length > 0) {
          this.showBulk = true;
        }
      } else {
        this.showBulk = false;
      }
    },

    performBulkAction(methodFunc) {
      if (methodFunc === "return Appraisal") {
        this.returnAppraisal("");
      }

      if (methodFunc === "approve Appraisal") {
        this.approveAppraisal("");
      }
    },

    handleShowDetails() {
      this.showDetails = !this.showDetails;
    },

    getTemplate() {
      const currentYear = Number(this.$route.params.year);
      this.$_getPerfomanceTemplate("").then((response) => {
        const templatesArr = response.data.PerformanceTemplate;

        templatesArr.filter((template) => {
          if (template.year === currentYear) {
            this.agreementData.title = template.title;
            this.agreementData.cycle = template.title;
            this.agreementData.description = template.agreement_description;
            this.agreementData.period.start = this.setDates(
              template.appraisal_cycle.agreement_start.replace(/\s+/g, '')
            ).toISOString();
            this.agreementData.period.end = this.setDates(
              template.appraisal_cycle.agreement_end.replace(/\s+/g, '')
            ).toISOString();
          }
          return {};
        });
      });
    },

    getAppraisalCycle() {
      const today = new Date();

      this.$_getAppraisalCycle(this.appraisalQuery()).then((response) => {
        const appraisal = response.data.AppraisalCycle;
        const { extension } = response.data;

        this.extendedCycle = extension;

        this.agreementData.description = appraisal[0].description;

        if (appraisal[0].cycles.length > 0) {
          const appraisalCycle = appraisal[0].cycles.sort(
            (a, b) =>
              this.setDates(a.appraisal_starts) -
              this.setDates(b.appraisal_starts)
          );

          appraisalCycle.map((cycle) => {
            this.cyclesArr.push(cycle.id);

            const cycleStartDate = new Date(
              this.formatDateTime(cycle.appraisal_starts)
            );

            const cycleEndDate = new Date(
              this.formatDateTime(cycle.appraisal_ends)
            );

            cycleEndDate.setDate(cycleEndDate.getDate() + 1);

            this.agreementCycles.push({
              name: cycle.name,
              start: this.formatDateTime(cycle.appraisal_starts),
              end: this.formatDateTime(cycle.appraisal_ends),
              active: today >= cycleStartDate && today <= cycleEndDate,
              id: cycle.id,
            });
            
            /* if (today >= cycleStartDate && today <= cycleEndDate) {
              this.activeCycle = cycle.name;
              } */
            });
            this.activeCycle = appraisalCycle[0].name;
            this.currentQAppraisal = appraisalCycle[0].id
            if(this.$route.params.QAppraisal){
              this.viewCycle(this.$route.params.QAppraisal)
            }
        }
        this.getDirectReports();
      });
    },

    getSelfScore(userId) {
      let score;
      this.sharedAppraisals.forEach((appraisal) => {
        if (
          appraisal.year === this.$route.params.year.toString() &&
          appraisal.employeeId === userId
        ) {
          const tabIndex = this.cyclesArr.indexOf(appraisal.cycleId, 0);

          if (tabIndex === this.cyclesArr.length - 1) {
            score = appraisal.selfScore;
          } else {
            score = this.midTermAppraisalStructure === 'regular_ratings' ? `${appraisal.employeeScore}` : `${appraisal.employeeFlag}`;
          }
        }
        return {};
      });
      return score || "---";
    },

    getManagerScore(userId) {
      let score = null;

      this.sharedAppraisals.forEach((appraisal) => {
        if (
          appraisal.year === this.$route.params.year.toString() &&
          appraisal.employeeId === userId
        ) {
          const tabIndex = this.cyclesArr.indexOf(appraisal.cycleId, 0);

          if (tabIndex === this.cyclesArr.length - 1) {
            score =
              appraisal.managerScore != null
                ? Number(appraisal.managerScore)
                : null;
          } else {
            score =
              this.midTermAppraisalStructure === "regular_ratings"
                ? appraisal.managerFlagScore ?? null
                : appraisal.managerFlag ?? null;
          }
        }
      });

      return score != null && score !== "" ? score : "---";
    },


    getDottedManagerScore(userId) {
      let score = null;

      this.sharedAppraisals.forEach((appraisal) => {
        if (
          appraisal.year === this.$route.params.year.toString() &&
          appraisal.employeeId === userId
        ) {
          const tabIndex = this.cyclesArr.indexOf(appraisal.cycleId, 0);

          if (tabIndex === this.cyclesArr.length - 1) {
            score =
              appraisal.dottedManagerScore != null
                ? Number(appraisal.dottedManagerScore)
                : null;
          } else {
            score =
              this.midTermAppraisalStructure === "regular_ratings"
                ? appraisal.dottedManagerFlagScore ?? null
                : appraisal.dottedManagerFlag ?? null;
          }
        }
      });

      return score != null && score !== "" ? score : "---";
    },

    getStatus(userId) {
      let status;
      this.sharedAppraisals.filter((appraisal) => {
        if (
          appraisal.year === this.$route.params.year.toString() &&
          appraisal.employeeId === userId
        ) {
          status = appraisal.appraisalStatus;
        }
        return {};
      });
      return status || "not submitted";
    },

    getAprovedBy(userId, isDottedManager) {
      let status;
      this.sharedAppraisals.forEach((appraisal) => {
        if (
          appraisal.year === this.$route.params.year.toString() &&
          appraisal.employeeId === userId
        ) {
          if(isDottedManager){
            status = appraisal.dottedLineManagerApprovedBy;
          }else{
            status = appraisal.managerApprovedBy;
          }
        }
      });
      return status ;
    },
    getReviewType(userId) {
      let status;
      this.sharedAppraisals.filter((appraisal) => {
        if (
          appraisal.year === this.$route.params.year.toString() &&
          appraisal.employeeId === userId
        ) {
          status = appraisal.reviewType;
        }
        return {};
      });
      return status || "";
    },

    getApprovedScore(userId) {
      let score;
      this.sharedAppraisals.forEach((appraisal) => {
        if (
          appraisal.year === this.$route.params.year.toString() &&
          appraisal.employeeId === userId &&
          appraisal.appraisalStatus === "submitted"
        ) {
          const tabIndex = this.cyclesArr.indexOf(appraisal.cycleId, 0);
          if (tabIndex === this.cyclesArr.length - 1) {
            score = appraisal.approvedScore;
          } else {
            score = this.midTermAppraisalStructure === 'regular_ratings' ? appraisal.approvedScore : `${appraisal.approvedScoreFlag}`;
          }
        }
      });
      return score || "---";
    },

    getDateSubmitted(userId) {
      let score;
      this.sharedAppraisals.forEach((appraisal) => {
        if (
          appraisal.year === this.$route.params.year.toString() &&
          appraisal.employeeId === userId
        ) {
          score = appraisal.dateSubmitted;
        }
        return {};
      });
      return score || "";
    },

    getGoalId(userId) {
      let score;
      this.sharedAppraisals.forEach((appraisal) => {
        if (
          appraisal.year === this.$route.params.year.toString() &&
          appraisal.employeeId === userId
        ) {
          score = appraisal.goalId;
        }
        return {};
      });
      return score || "";
    },

    getCycleName(userId) {
      let score;
      this.sharedAppraisals.forEach((appraisal) => {
        if (
          appraisal.year === this.$route.params.year.toString() &&
          appraisal.employeeId === userId
        ) {
          score = appraisal.cycleName;
        }
        return {};
      });
      return score || "";
    },

    getAppraisalId(userId) {
      let score;
      this.sharedAppraisals.forEach((appraisal) => {
        if (
          appraisal.year === this.$route.params.year.toString() &&
          appraisal.employeeId === userId
        ) {
          score = appraisal.id;
        }
        return {};
      });
      return score || "";
    },

    getUsersDirectReports(params) {
      this.senderId = this.$AuthUser;
      const currentYear = new Date().getFullYear();
      this.loadingTable = true;
      this.employees = [];

      this.$_getDirectReportsAgreementsList(
        this.$AuthUser.id,
        currentYear,
        params
      )
        .then((response) => {
          const employees = response.data.directReports;
          this.metaData = response.data.meta;
          employees.forEach((data) => {
            this.employees.push({
              id: data.userId,
              name: `${data.fname} ${data.lname}`,
              photo: data.photo,
              function: data.function,
              designation: data.designation,
              selfAppraisal: this.getSelfScore(data.userId),
              managerAppraisal: this.getManagerScore(data.userId) || '---',
              dottedLineManagerAppraisal: this.getDottedManagerScore(data.userId) || '---',
              status: this.getStatus(data.userId),
              approvedScore: this.getApprovedScore(data.userId),
              submitted: this.getDateSubmitted(data.userId),
              reviewType: this.getReviewType(data.userId),
              goal: this.getGoalId(data.userId),
              cycleName: this.getCycleName(data.userId),
              year: this.$route.params.year,
              appraisalId: this.getAppraisalId(data.userId),
              agreementId: data.agreementId,
              isDottedReporting: data.isDottedReporting,
              hasDotted: data.hasDotted,
              dottedApprovedBy: this.getAprovedBy(data.userId, true),
              managerApprovedBy: this.getAprovedBy(data.userId, false),
            });
            return {};
          });
          this.isLoading = false;
          this.loadingTable = false;
        })
        .catch(() => {
          this.isLoading = false;
          this.loadingTable = false;
        });
    },

    getDirectReports() {
      this.$_getDirectReportAppraisals(this.$AuthUser.id).then((response) => {
        const appraisalData = response.data.sharedAppraisals;
        appraisalData.map((appraisal) => {
          appraisal.shared_appraisal.map((shared) => {
            if (shared.appraisal.cycleId) {
              this.agreementCycles.filter((cycle) => {
                if (
                  cycle.id === shared.appraisal.cycleId &&
                  cycle.name === this.activeCycle
                ) {
                  this.sharedAppraisals.push({
                    id: shared.appraisal.id,
                    appraisalAgreement: shared.appraisal.appraisal_agreement,
                    appraisalState: shared.appraisal.appraisal_state,
                    appraisalStatus: shared.appraisal.appraisal_status,
                    dateSubmitted: shared.appraisal.date_submitted,
                    agreementId: shared.appraisal.employeeAgreementId,
                    employeeId: shared.appraisal.employeeId,
                    managerScore: shared.appraisal.manager_score,
                    selfScore: shared.appraisal.self_score,
                    reviewType: shared.appraisal.reviewType,
                    year: shared.appraisal.year,
                    goalId: shared.appraisal.goalId,
                    employeeFlag: shared.appraisalSummary.employeeFlag,
                    employeeScore: shared.appraisalSummary.employeeFlagScore,
                    managerFlagScore: shared.appraisalSummary.managerFlagScore,
                    managerFlag: shared.appraisalSummary.managerFlag,
                    dottedManagerScore: shared.appraisal.dotted_line_manager_score,
                    dottedManagerFlagScore: shared.appraisalSummary.dottedLineManagerFlagScore,
                    dottedManagerFlag: shared.appraisalSummary.dottedLineManagerFlag,
                    approvedScore: shared.appraisal.approved_score,
                    approvedScoreFlag: shared.appraisalSummary.approvedScoreFlag,
                    cycleId: cycle.id,
                    cycleName: cycle.name,
                    dottedLineManagerApprovedBy: shared.appraisal.dotted_line_manager_approved_by,
                    managerApprovedBy: shared.appraisal.manager_approved_by
                  });
                }
                return {};
              });
            }
            return {};
          });
          return {};
        });

        this.getUsersDirectReports("");
      });
    },

    approveAppraisal(appraisalData) {
      // Base payload shared by all requests
      const basePayload = {
        appraisal_status: "submitted",
        userId: this.$AuthUser.id,
        orgId: this.$orgId,
      };
      if (appraisalData && typeof appraisalData === "object") {
        const requestPayload = {
          ...basePayload,
          hasDotted: appraisalData.hasDotted,
          appraisalIds: [appraisalData.appraisalId],
          type: appraisalData.isDottedReporting
            ? "dotted_line_managers"
            : "direct_line_managers",
        };

        this.sendApprovalRequest(requestPayload);
        return;
      }

      const directLineIds = [];
      const dottedLineIds = [];

      this.selectedEmployees.forEach((employee) => {
        if (employee.appraisalId) {
          if (employee.isDottedReporting) {
            dottedLineIds.push(employee.appraisalId);
          } else {
            directLineIds.push(employee.appraisalId);
          }
        }
      });

      const requests = [];

      if (directLineIds.length > 0) {
        requests.push(
          this.sendApprovalRequest({
            ...basePayload,
            appraisalIds: directLineIds,
            type: "direct_line_managers",
          })
        );
      }

      if (dottedLineIds.length > 0) {
        requests.push(
          this.sendApprovalRequest({
            ...basePayload,
            appraisalIds: dottedLineIds,
            type: "dotted_line_managers",
          })
        );
      }

      Promise.all(requests).catch((err) => {
        this.$toasted.error(
          err.response?.data?.message || "Error approving appraisals",
          { duration: 5000 }
        );
      });
    },


    async sendApprovalRequest(payload) {
      try {
        const response = await this.$_approveAppraisal(payload);
        this.$toasted.success(response.data.message, { duration: 5000 });

        // reset state after each approval group
        this.templateArr = [];
        this.cyclesArr = [];
        this.agreementCycles = [];
        this.sharedAppraisals = [];
        this.employees = [];
        this.isLoading = true;

        this.getTemplate();
        this.getAppraisalCycle();
      } catch (err) {
        this.$toasted.error(err.response?.data?.message || "Approval failed", {
          duration: 5000,
        });
      }
    },

    returnAppraisal(appraisalId) {
      const requestPayload = {
        appraisalIds: [appraisalId],
      };
      this.$_returnAppraisal(requestPayload)
        .then((response) => {
          this.$toasted.success(response.data.message, { duration: 5000 });
          this.templateArr = [];
          this.cyclesArr = [];
          this.agreementCycles = [];
          this.sharedAppraisals = [];
          this.employees = [];
          this.isLoading = true;
          this.getTemplate();
          this.getAppraisalCycle();
        })
        .catch((err) => {
          this.$toasted.error(err.response.data.message, { duration: 5000 });
        });
    },

    getComments(agreementId, userId, kpi = '') {
      this.allComments = [];
      this.receiverId = userId;
      this.commentAgreementId = agreementId;
      this.loadComments = true;
      this.$_getEachAgreementComments(agreementId, kpi)
        .then((response) => {
          const { comments } = response.data;
          this.allComments = comments;
          this.commentModal = true;
          this.loadComments = false;
        })
        .catch(() => {
          this.loadComments = false;
          this.$toasted.error('Error getting comments', { duration: 5000 });
        });
    },
    sendComment() {
      const payload = {
        performanceAgreementId: this.commentAgreementId,
        senderId: this.$AuthUser.id,
        receiverId: this.receiverId,
        comment: this.comment,
        sendTo: "admin",
      };
      this.$_sendAgreementComments(payload).then(() => {
        this.allComments = [];
        this.comment = '';
        this.commentModal = false
        this.$toasted.success('Comment successfully sent', { duration: 5000 });
      })
        .catch(() => {
          this.$toasted.error('Error sending comment', { duration: 5000 });
        });
    },

    deleteComments(id) {
      this.$_deleteAgreementComments(id)
        .then((response) => {
          this.$toasted.success(response.data.message, { duration: 5000 });
          // this.getComments(this.commentAgreementId);
        })
        .then((err) => {
          this.$toasted.error(err.response.data.message, { duration: 5000 });
        });
    },

    formatCommentTime(date) {
      const newDate = new Date(date);
      const hours = newDate.getUTCHours();
      const minutes = newDate.getMinutes();
      return `${1 + ((hours - 1) % 12)}:${minutes
        .toString()
        .padStart(2, "0")} ${hours > 11 ? "PM" : "AM"}`;
    },
    formatCommentDate(date) {
      const newDate = new Date(date);
      return newDate.toDateString();
    },
  },
  created() {
    this.getTemplate();
  },
  async mounted() {
    await this.getAppraisalCycle();
    await this.$_getPerformanceGeneralSetting().then((response) => {
      response.data.PerformanceSettings.forEach((setting) => {
        this.midTermAppraisalStructure = setting.midTermAppraisalMethod;
        this.dottedReportingAppraisal = response.data.PerformanceSettings[0].appraisalReviewer.includes('dotted_line_managers');
        if (this.dottedReportingAppraisal) {
          const obj = { title: "DottedLine Manager", value: "dottedLineManagerAppraisal" }
          this.AppHeader.splice(4, 0, obj);
        }
        if (setting.appraisailScoringPreference === "manager_to_appraisee") {
          this.openCommentsAction = true;
          this.openReviewAction = true;
        }
      });
    });
  },
};
</script>

<style scoped>
.pending {
  background: rgba(233, 147, 35, 0.08);
  color: #e99323;
  text-align: center;
  padding: 8px;
  border-radius: 4px;
}

.approved {
  background: rgba(19, 181, 106, 0.08);
  color: #13b56a;
  text-align: center;
  padding: 8px;
  border-radius: 4px;
}

.disapproved {
  background: rgba(234, 60, 83, 0.08);
  color: #ea3c53;
  text-align: center;
  padding: 8px;
  border-radius: 4px;
}

.submitted {
  background: rgba(50, 28, 59, 0.15);
  text-align: center;
  padding: 8px;
  border-radius: 4px;
}

.not_submitted {
  background: rgba(234, 60, 83, 0.15);
  color: rgba(234, 60, 83, 1);
  text-align: center;
  padding: 8px;
  border-radius: 4px;
}
</style>
